import { Injectable, inject } from '@angular/core';
import { BaseRequester } from './common/base-requester';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNil as _isNil } from 'lodash';
import {
    AppConfigStoreService,
    ConfigStoreResponse,
} from './app-config-store.service';

// The reordering ROP/EOQ settings live under this key in the App Config Store
// (management-console.reordering); purchase orders and ROP overrides own
// their keys in PurchaseOrderService / RopOverrideService.
const REORDERING_CONFIG_KEY = 'default';

// lnw_crawler.inventory filter for the products that get reordered.
export const ORDERABLE_PRODUCTS_QUERY = {
    is_show: true,
    // Leave out parent products: sales live on their child variants, so a
    // parent's remain would read as stock with no demand. has_sub is LnwShop's
    // own flag; product_type is only what the crawler derives from the page's
    // shape (GLP-175).
    has_sub: false,
};

@Injectable({
    providedIn: 'root',
})
export class ReorderingService extends BaseRequester {
    private configStore = inject(AppConfigStoreService);

    constructor() {
        super();
    }

    // Reorder Point (ROP) lambda — "when to reorder".
    public fetchRop(
        filter: object,
        calculationDays: number,
        orderingLeadTimeDays: number,
        serviceLevel?: number,
    ): Observable<RopResponse> {
        const payload: RopRequest = {
            filter,
            calculationDays,
            orderingLeadTimeDays,
        };
        if (!_isNil(serviceLevel)) {
            payload.serviceLevel = serviceLevel;
        }
        return this.internalPostRequest<RopRequest>(
            environment.ropEndpoint,
            payload,
        ).pipe(map((response) => this.serviceHandler<RopResponse>(response)));
    }

    // Economic Order Quantity (EOQ) lambda — "how much to reorder".
    public fetchEoq(
        filter: object,
        calculationDays: number,
        orderingCost: number,
        holdingRate: number,
        costRatio?: number,
    ): Observable<EoqResponse> {
        const payload: EoqRequest = {
            filter,
            calculationDays,
            orderingCost,
            holdingRate,
        };
        if (!_isNil(costRatio)) {
            payload.costRatio = costRatio;
        }
        return this.internalPostRequest<EoqRequest>(
            environment.eoqingEndpoint,
            payload,
        ).pipe(map((response) => this.serviceHandler<EoqResponse>(response)));
    }

    // Saved reordering settings; null until the first save.
    public getConfig(): Observable<ReorderingConfig | null> {
        return this.configStore.get<ReorderingConfig>(REORDERING_CONFIG_KEY);
    }

    // Persist (upsert) the reordering settings.
    public saveConfig(
        config: ReorderingConfig,
    ): Observable<ConfigStoreResponse<ReorderingConfig>> {
        return this.configStore.set(REORDERING_CONFIG_KEY, config);
    }
}

interface RopRequest {
    filter: object;
    calculationDays: number;
    orderingLeadTimeDays: number;
    serviceLevel?: number;
}

interface EoqRequest {
    filter: object;
    calculationDays: number;
    orderingCost: number;
    holdingRate: number;
    costRatio?: number;
}

export interface RopResponse {
    request_param: object;
    service_factor_z: number;
    reordering_result: RopRow[];
}

export interface RopRow {
    _id: number; // pid (from $group)
    unit_sold: number;
    data_days: number;
    demand_std_dev: number | null;
    safety_stock: number | null;
    rop: number | null;
    note?: string;
}

export interface EoqResponse {
    request_param: object;
    eoq_result: EoqRow[];
}

export interface EoqRow {
    // Identity/price fields (sku, title, list_price) are intentionally NOT
    // returned by the lambda — source those from the inventory view and merge
    // EOQ's own metrics by pid.
    pid: number;
    unit_sold: number;
    data_days: number;
    annual_demand: number | null;
    unit_value: number;
    annual_holding_cost_per_unit?: number;
    eoq: number | null;
    eoq_units?: number | null;
    orders_per_year?: number;
    order_cycle_days?: number;
    total_annual_variable_cost?: number;
    note?: string;
}

// The reordering UI settings persisted via the App Config Store lambda.
export interface ReorderingConfig {
    calculationDays?: number;
    orderingLeadTimeDays?: number;
    demandForDays?: number;
    // Per-ABC-class cycle service levels sizing the ROP safety stock;
    // "Other" covers the remaining classes (C/D/N/unclassified).
    // 0.5 ⇒ Z=0 ⇒ no safety stock.
    serviceLevelA?: number;
    serviceLevelB?: number;
    serviceLevelOther?: number;
    // Legacy single service level from configs saved before the per-class
    // split — used as the fallback for any per-class level the stored config
    // lacks; never written back.
    serviceLevel?: number;
    orderingCost?: number;
    holdingRate?: number;
    costRatio?: number;
}
